/**
 * IndexedDB-backed blob storage for photos.
 * Stores compressed photo blobs separately from mutation metadata
 * for better IndexedDB performance with large binary data.
 */

import { openDatabase, getStore, promisify } from '../db/open';
import { newClientId } from '../ids/newClientId';

const STORE_NAME = 'blobs';
// v2 adds the `ownerKey` index so blobs can be grouped/evicted by a stable
// owner (e.g. a form submissionId) independent of any single mutation id.
const DB_VERSION = 2;
const DEFAULT_MAX_STORAGE_BYTES = 50 * 1024 * 1024; // 50 MB

export class OfflineStorageFullError extends Error {
  /** Bytes in use at the moment this was thrown/constructed. */
  readonly currentBytes: number;
  /** The cap that was hit. */
  readonly maxBytes: number;

  constructor(currentBytes: number, maxBytes: number) {
    super(`Offline storage full (${Math.round(currentBytes / 1024 / 1024)}MB / ${Math.round(maxBytes / 1024 / 1024)}MB). Sync pending photos before capturing more.`);
    this.name = 'OfflineStorageFullError';
    this.currentBytes = currentBytes;
    this.maxBytes = maxBytes;
  }
}

function sanitizeFileName(fileName: string): string {
  return fileName
    .replace(/[/\\]/g, '_')
    .replace(/\.\./g, '_')
    .replace(/\0/g, '');
}

export interface StoredBlob {
  readonly id: string;
  readonly mutationId: string;
  /**
   * Stable owner of this blob (e.g. a form submissionId), decoupled from the
   * mutation id. Null for legacy/non-owner blobs; such blobs are not indexed
   * by ownerKey and are unaffected by getBlobsByOwner/deleteBlobsByOwner.
   */
  readonly ownerKey: string | null;
  readonly blob: Blob;
  readonly fileName: string;
  readonly width: number;
  readonly height: number;
  readonly originalSize: number;
  readonly createdAt: number;
}

export class PhotoStore {
  private readonly dbName: string;
  /** The cap this store enforces. Public so callers can pre-flight a batch
   * against it (see getAvailableBytes) without duplicating the constant. */
  readonly maxStorageBytes: number;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string, maxStorageBytes: number = DEFAULT_MAX_STORAGE_BYTES) {
    this.dbName = dbName;
    this.maxStorageBytes = maxStorageBytes;
  }

  private getDB(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDatabase(this.dbName, DB_VERSION, (db, _oldVersion, tx) => {
        // Fresh DB: create the store with both indexes.
        // Existing v1 DB (oldVersion === 1): add the ownerKey index to the
        // existing store via the versionchange transaction. Legacy blobs have
        // no ownerKey property, so they are simply absent from the new index.
        const store = db.objectStoreNames.contains(STORE_NAME)
          ? tx.objectStore(STORE_NAME)
          : (() => {
              const s = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
              s.createIndex('mutationId', 'mutationId', { unique: false });
              return s;
            })();
        if (!store.indexNames.contains('ownerKey')) {
          store.createIndex('ownerKey', 'ownerKey', { unique: false });
        }
      }).then((db) => {
        // If another tab needs to upgrade this DB (e.g. a future schema bump),
        // close our connection so it isn't blocked; drop the cache so the next
        // call reopens at the new version.
        db.onversionchange = () => {
          db.close();
          this.dbPromise = null;
        };
        return db;
      });
    }
    return this.dbPromise;
  }

  /** Store a compressed photo blob. Returns the generated ID.
   * Pass options.ownerKey to make the blob evictable by owner (decoupled
   * from mutationId) via getBlobsByOwner/deleteBlobsByOwner. */
  async addBlob(
    mutationId: string,
    blob: Blob,
    metadata: {
      readonly fileName: string;
      readonly width: number;
      readonly height: number;
      readonly originalSize: number;
    },
    options: { readonly ownerKey?: string } = {},
  ): Promise<string> {
    const currentSize = await this.getTotalSize();
    if (currentSize + blob.size > this.maxStorageBytes) {
      throw new OfflineStorageFullError(currentSize, this.maxStorageBytes);
    }
    const id = newClientId();
    const entry: StoredBlob = {
      id,
      mutationId,
      ownerKey: options.ownerKey ?? null,
      blob,
      fileName: sanitizeFileName(metadata.fileName),
      width: metadata.width,
      height: metadata.height,
      originalSize: metadata.originalSize,
      createdAt: Date.now(),
    };
    const db = await this.getDB();
    await promisify(getStore(db, STORE_NAME, 'readwrite').put(entry));
    return id;
  }

  /** Get a single blob by ID. */
  async getBlob(id: string): Promise<StoredBlob | undefined> {
    const db = await this.getDB();
    return promisify(getStore(db, STORE_NAME, 'readonly').get(id));
  }

  /** Get all blobs for a mutation. */
  async getBlobsByMutation(mutationId: string): Promise<readonly StoredBlob[]> {
    const db = await this.getDB();
    const index = getStore(db, STORE_NAME, 'readonly').index('mutationId');
    return promisify(index.getAll(mutationId));
  }

  /** Get all blobs owned by ownerKey (e.g. a form submissionId). */
  async getBlobsByOwner(ownerKey: string): Promise<readonly StoredBlob[]> {
    const db = await this.getDB();
    const index = getStore(db, STORE_NAME, 'readonly').index('ownerKey');
    return promisify(index.getAll(ownerKey));
  }

  /** Delete a single blob. */
  async deleteBlob(id: string): Promise<void> {
    const db = await this.getDB();
    await promisify(getStore(db, STORE_NAME, 'readwrite').delete(id));
  }

  /** Delete all blobs for a mutation.
   * Uses a single transaction with cursor to avoid auto-commit races. */
  async deleteBlobsByMutation(mutationId: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const index = tx.objectStore(STORE_NAME).index('mutationId');
      const cursorReq = index.openCursor(mutationId);
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Delete all blobs owned by ownerKey.
   * Uses a single transaction with cursor to avoid auto-commit races. */
  async deleteBlobsByOwner(ownerKey: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const index = tx.objectStore(STORE_NAME).index('ownerKey');
      const cursorReq = index.openCursor(ownerKey);
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Get total storage size in bytes. */
  async getTotalSize(): Promise<number> {
    const db = await this.getDB();
    const all = await promisify(getStore(db, STORE_NAME, 'readonly').getAll()) as StoredBlob[];
    return all.reduce((sum, entry) => sum + (entry.blob?.size ?? 0), 0);
  }

  /** Get every stored blob, any owner/mutation/status. Used by the orphan
   * blob sweep to decide which blobs nothing references anymore. */
  async getAllBlobs(): Promise<readonly StoredBlob[]> {
    const db = await this.getDB();
    return promisify(getStore(db, STORE_NAME, 'readonly').getAll());
  }

  /** Bytes of headroom left under the cap, floored at 0. A caller can use
   * this to decide how much of a pending batch fits BEFORE calling addBlob,
   * instead of finding out one blob at a time via OfflineStorageFullError. */
  async getAvailableBytes(): Promise<number> {
    const used = await this.getTotalSize();
    return Math.max(0, this.maxStorageBytes - used);
  }

  /** Clear all stored blobs. */
  async clear(): Promise<void> {
    const db = await this.getDB();
    await promisify(getStore(db, STORE_NAME, 'readwrite').clear());
  }
}
